/**
 * Reports API Service - EVOP Inspector
 *
 * Provides endpoints for fetching admin reports.
 */

import { apiClient } from './api'

// Report types
export interface SyncSummary {
  total: number
  completed: number
  failed: number
  cancelled: number
  running: number
  records_processed: number
  avg_duration_seconds: number
  by_system: Record<string, { total: number; completed: number; failed: number }>
}

export interface HealthOverview {
  totals: Record<string, number>
  by_domain: Record<string, Record<string, number>>
}

export interface ConnectivityStatus {
  totals: Record<string, number>
  by_system: Record<string, Record<string, number>>
}

export interface ActivityHighlights {
  logins: number
  manual_syncs: number
  user_management_actions: number
  failed_operations: number
  feedback_received: number
  by_category: Record<string, number>
}

export interface RecentLogin {
  email: string
  success: boolean
  timestamp: string
  reason?: string | null
}

export interface UserLogins {
  successful: number
  failed: number
  recent: RecentLogin[]
}

export interface MaintenanceSummary {
  token_refreshes_success: number
  token_refreshes_failed: number
  health_checks: number
  stale_data_detected: number
  syncs_triggered: number
  cleanups: number
  vacuums: number
}

export interface AttentionItem {
  type: string
  severity: 'error' | 'warning' | 'info'
  fp_code?: string
  fp_name?: string
  system?: string
  band?: string
  message: string
  link: string
  timestamp?: string | null
}

export interface DailyReport {
  report_date: string
  generated_at: string
  sync_summary: SyncSummary
  health_overview: HealthOverview
  connectivity: ConnectivityStatus
  activity_highlights: ActivityHighlights
  user_logins?: UserLogins
  maintenance: MaintenanceSummary
  attention_required: AttentionItem[]
  new_exceptions_count: number
}

export type TimeMode = 'today' | 'yesterday' | 'custom'

export interface SystemStatusResponse {
  mode: string
  report_date: string
  generated_at: string
  sync_summary: SyncSummary
  health_overview: HealthOverview
  connectivity: ConnectivityStatus
  activity_highlights: ActivityHighlights
  user_logins: UserLogins
  maintenance: MaintenanceSummary
  attention_required: AttentionItem[]
  new_exceptions_count: number
}

class ReportsApi {
  /**
   * Get the daily admin report
   * @param date - Report date in YYYY-MM-DD format (defaults to yesterday)
   */
  async getDailyReport(date?: string): Promise<DailyReport> {
    const params = date ? { report_date: date } : {}
    const response = await apiClient.get<DailyReport>('/admin/reports/daily', {
      params
    })
    return response.data
  }

  /**
   * Get historical system status for a specific date
   * @param date - Report date in YYYY-MM-DD format
   */
  async getHistoricalStatus(date: string): Promise<SystemStatusResponse> {
    const response = await apiClient.get<SystemStatusResponse>('/admin/system-status/historical', {
      params: { report_date: date }
    })
    return response.data
  }
}

export const reportsApi = new ReportsApi()
